/**
 * CameraRig Component
 *
 * Cinemachine-like camera controller that provides procedural camera behaviors.
 * Supports tracking targets, damping, dead zones, and various follow/look-at modes.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  runtime,
  customEditor,
} from "../attributes";

/** Standby Update Mode */
export enum StandbyUpdateMode {
  /** Only update when camera is live */
  Never = 0,
  /** Always update every frame */
  Always = 1,
  /** Update occasionally (round robin) */
  RoundRobin = 2,
}

/** Blend Hint Flags */
export enum BlendHint {
  None = 0,
  /** Spherical position interpolation */
  SphericalPosition = 1 << 0,
  /** Cylindrical position interpolation */
  CylindricalPosition = 1 << 1,
  /** Screen space aim interpolation */
  ScreenSpaceAimWhenTargetsDiffer = 1 << 2,
  /** Inherit position from current camera */
  InheritPosition = 1 << 3,
  /** Ignore target during rotation blend */
  IgnoreTarget = 1 << 4,
  /** Freeze state when blending out */
  FreezeWhenBlendingOut = 1 << 5,
}

/** Position Control Mode */
export enum PositionControlMode {
  /** No position control */
  None = 0,
  /** Simple follow with fixed offset */
  Follow = 1,
  /** Follow with damping and dead zone */
  PositionComposer = 2,
  /** Hard lock to target position */
  HardLockToTarget = 3,
  /** Move along a spline */
  SplineDolly = 4,
  /** Orbit around target */
  Orbital = 5,
}

/** Rotation Control Mode */
export enum RotationControlMode {
  /** No rotation control */
  None = 0,
  /** Composited look-at with damping */
  RotationComposer = 1,
  /** Always center on look-at target */
  HardLookAt = 2,
  /** Match tracking target rotation */
  RotateWithFollowTarget = 3,
}

@component("core.CameraRig")
@customEditor("CameraRigEditor")
@displayName("Camera Rig")
@icon("Clapperboard")
@color("#9b59b6")
@category("Rendering")
@allowMultiple(false)
@requireComponent(["core.Transform", "core.Camera2D"])
export class CameraRig {
  // ========================================================================
  // PRIORITY & CHANNEL
  // ========================================================================

  /** Temporarily make this camera live for preview */
  @field({ type: "bool", label: "Solo", group: "Priority" })
  solo: boolean = false;

  /** Enable priority-based camera selection */
  @field({ type: "bool", label: "Use Priority", group: "Priority" })
  usePriority: boolean = true;

  /** Channel for multi-brain setups (e.g., split-screen) */
  @field({ type: "i32", label: "Channel", min: 0, max: 15, group: "Priority" })
  channel: number = 0;

  /** Priority for camera selection (higher = more important) */
  @field({ type: "i32", label: "Priority", min: -100, max: 100, group: "Priority" })
  priority: number = 0;

  // ========================================================================
  // STANDBY & BLENDING
  // ========================================================================

  /** How often to update when not live */
  @field({ type: "i32", label: "Standby Update", tooltip: "0=Never, 1=Always, 2=RoundRobin" })
  standbyUpdate: number = StandbyUpdateMode.Always;

  /** Blend hint flags (bitmask) */
  @field({ type: "i32", label: "Blend Hints", hidden: true })
  blendHints: number = BlendHint.None;

  /** Blend time when transitioning to this camera (seconds) */
  @field({ type: "f32", label: "Blend In Time", min: 0, max: 10, step: 0.1 })
  blendInTime: number = 1.0;

  // ========================================================================
  // TARGETS
  // ========================================================================

  /** Target entity GUID to track/follow (empty = none) */
  @field({ type: "string", label: "Tracking Target", tooltip: "GameObject GUID to follow" })
  trackingTarget: string = "";

  /** Separate look-at target entity GUID (empty = same as tracking target) */
  @field({ type: "string", label: "Look At Target", tooltip: "GameObject GUID to look at (empty = use tracking target)" })
  lookAtTarget: string = "";

  // ========================================================================
  // POSITION CONTROL
  // ========================================================================

  /** Position control mode */
  @field({ type: "i32", label: "Position Mode", tooltip: "0=None, 1=Follow, 2=Composer, 3=HardLock, 4=Spline" })
  positionMode: number = PositionControlMode.PositionComposer;

  /** Follow offset from target */
  @field({ type: "object", label: "Follow Offset", hidden: true })
  followOffset: { x: number; y: number } = { x: 0, y: 0 };

  /** Damping factor for position smoothing (0 = instant, higher = smoother) */
  @field({ type: "f32", label: "Position Damping", min: 0, max: 10, step: 0.1 })
  positionDamping: number = 1.0;

  /** Separate X damping (if different from Y) */
  @field({ type: "f32", label: "Damping X", min: 0, max: 10, step: 0.1, hidden: true })
  dampingX: number = 1.0;

  /** Separate Y damping */
  @field({ type: "f32", label: "Damping Y", min: 0, max: 10, step: 0.1, hidden: true })
  dampingY: number = 1.0;

  // ========================================================================
  // SPLINE DOLLY
  // ========================================================================

  /** GUID of the Spline2D object to follow (for SplineDolly mode) */
  @field({ type: "string", label: "Spline Path", tooltip: "GameObject GUID with Spline2D component" })
  splinePath: string = "";

  /** How to position on spline: 0=ClosestToTarget, 1=TargetForward, 2=Manual, 3=AutoScroll */
  @field({ type: "i32", label: "Spline Position Mode", tooltip: "0=ClosestToTarget, 1=TargetForward, 2=Manual, 3=AutoScroll" })
  splinePositionMode: number = 0;

  /** Manual spline position (0 to segment count) when mode=Manual */
  @field({ type: "f32", label: "Spline Position", min: 0, max: 1000, step: 0.01 })
  splinePosition: number = 0;

  /** Auto-scroll speed (T units per second) for AutoScroll mode */
  @field({ type: "f32", label: "Auto Scroll Speed", min: -10, max: 10, step: 0.1 })
  autoScrollSpeed: number = 1.0;

  /** Damping for movement along spline */
  @field({ type: "f32", label: "Spline Damping", min: 0, max: 10, step: 0.1 })
  splineDamping: number = 1.0;

  /** Auto-dolly speed (units per second) when target moves - 0 = instant */
  @field({ type: "f32", label: "Auto Dolly Speed", min: 0, max: 100, step: 0.1 })
  autoDollySpeed: number = 0;

  /** Whether to align camera rotation to spline tangent */
  @field({ type: "bool", label: "Align To Spline" })
  alignToSpline: boolean = false;

  // ========================================================================
  // ORBITAL MODE
  // ========================================================================

  /** Current orbit angle in degrees (0 = right, 90 = top, 180 = left, 270 = bottom) */
  @field({ type: "f32", label: "Orbit Angle", min: -360, max: 360, step: 1 })
  orbitAngle: number = 0;

  /** Distance from target */
  @field({ type: "f32", label: "Orbit Radius", min: 0, max: 100, step: 0.1 })
  orbitRadius: number = 5;

  /** Vertical offset from target (for 2.5D perspective) */
  @field({ type: "f32", label: "Orbit Height", min: -50, max: 50, step: 0.1 })
  orbitHeight: number = 0;

  /** Orbit rotation speed (degrees per second) when controlled by input */
  @field({ type: "f32", label: "Orbit Speed", min: 0, max: 720, step: 1 })
  orbitSpeed: number = 180;

  /** Damping for orbit angle changes */
  @field({ type: "f32", label: "Orbit Damping", min: 0, max: 5, step: 0.1 })
  orbitDamping: number = 0.3;

  // ========================================================================
  // RECENTERING
  // ========================================================================

  /** Enable automatic recentering to default position */
  @field({ type: "bool", label: "Recenter Enabled" })
  recenterEnabled: boolean = false;

  /** Time to wait before recentering starts (seconds) */
  @field({ type: "f32", label: "Recenter Wait Time", min: 0, max: 10, step: 0.1 })
  recenterWaitTime: number = 1.0;

  /** Time to complete recentering (seconds) */
  @field({ type: "f32", label: "Recenter Time", min: 0.1, max: 10, step: 0.1 })
  recenterTime: number = 2.0;

  /** Default angle to recenter to (for Orbital mode) */
  @field({ type: "f32", label: "Recenter Angle", min: -360, max: 360, step: 1 })
  recenterAngle: number = 0;

  // ========================================================================
  // TARGET GROUP
  // ========================================================================

  /** GUID of TargetGroup component to use instead of single target */
  @field({ type: "string", label: "Target Group", tooltip: "GameObject GUID with TargetGroup component" })
  targetGroup: string = "";

  /** Use target group's suggested zoom */
  @field({ type: "bool", label: "Use Group Zoom" })
  useGroupZoom: boolean = true;

  // ========================================================================
  // DEAD ZONE & SOFT ZONE (Screen Composer)
  // ========================================================================

  /** Dead zone width (0-1, screen fraction) - target can move within without camera moving */
  @field({ type: "f32", label: "Dead Zone Width", min: 0, max: 1, step: 0.01 })
  deadZoneWidth: number = 0.1;

  /** Dead zone height (0-1, screen fraction) */
  @field({ type: "f32", label: "Dead Zone Height", min: 0, max: 1, step: 0.01 })
  deadZoneHeight: number = 0.1;

  /** Dead zone center offset X (-0.5 to 0.5) */
  @field({ type: "f32", label: "Dead Zone Offset X", min: -0.5, max: 0.5, step: 0.01 })
  deadZoneOffsetX: number = 0;

  /** Dead zone center offset Y (-0.5 to 0.5) */
  @field({ type: "f32", label: "Dead Zone Offset Y", min: -0.5, max: 0.5, step: 0.01 })
  deadZoneOffsetY: number = 0;

  /** Soft zone width (0-1) - damped following within this zone */
  @field({ type: "f32", label: "Soft Zone Width", min: 0, max: 2, step: 0.01 })
  softZoneWidth: number = 0.8;

  /** Soft zone height (0-1) */
  @field({ type: "f32", label: "Soft Zone Height", min: 0, max: 2, step: 0.01 })
  softZoneHeight: number = 0.8;

  // ========================================================================
  // ROTATION CONTROL
  // ========================================================================

  /** Rotation control mode */
  @field({ type: "i32", label: "Rotation Mode", tooltip: "0=None, 1=Composer, 2=HardLook, 3=RotateWithTarget" })
  rotationMode: number = RotationControlMode.None;

  /** Rotation damping for smooth look-at */
  @field({ type: "f32", label: "Rotation Damping", min: 0, max: 10, step: 0.1 })
  rotationDamping: number = 0.5;

  /** Dutch angle (camera tilt in degrees) */
  @field({ type: "f32", label: "Dutch Angle", min: -45, max: 45, step: 0.5 })
  dutchAngle: number = 0;

  // ========================================================================
  // LENS OVERRIDE
  // ========================================================================

  /** Override the attached Camera2D lens settings */
  @field({ type: "bool", label: "Override Lens" })
  overrideLens: boolean = false;

  /** Target orthographic size when override is enabled */
  @field({ type: "f32", label: "Ortho Size", min: 0.1, max: 100, step: 0.1 })
  targetOrthoSize: number = 5;

  /** Lens damping for smooth zoom transitions */
  @field({ type: "f32", label: "Lens Damping", min: 0, max: 10, step: 0.1 })
  lensDamping: number = 1.0;

  // ========================================================================
  // NOISE (Camera Shake)
  // ========================================================================

  /** Enable procedural noise/shake */
  @field({ type: "bool", label: "Enable Noise", group: "Noise" })
  noiseEnabled: boolean = false;

  /** Noise amplitude (position shake intensity) */
  @field({ type: "f32", label: "Noise Amplitude", min: 0, max: 5, step: 0.01, group: "Noise" })
  noiseAmplitude: number = 0.5;

  /** Noise frequency (shake speed) */
  @field({ type: "f32", label: "Noise Frequency", min: 0, max: 20, step: 0.1, group: "Noise" })
  noiseFrequency: number = 4.0;

  /** Rotation noise amplitude (degrees) */
  @field({ type: "f32", label: "Rotation Noise", min: 0, max: 10, step: 0.1, group: "Noise" })
  noiseRotationAmplitude: number = 0;

  // ========================================================================
  // BOUNDS & CONFINE
  // ========================================================================

  /** Enable camera bounds/confine */
  @field({ type: "bool", label: "Confine Camera", group: "Confine" })
  confineEnabled: boolean = false;

  /** Confine mode: 0 = soft (damped), 1 = hard (instant) */
  @field({ type: "i32", label: "Confine Mode", min: 0, max: 1, group: "Confine" })
  confineMode: number = 0;

  /** Confine damping for soft mode */
  @field({ type: "f32", label: "Confine Damping", min: 0, max: 10, step: 0.1, group: "Confine" })
  confineDamping: number = 0.5;

  /** Bounds min X */
  @field({ type: "f32", label: "Bounds Min X", group: "Confine" })
  boundsMinX: number = -100;

  /** Bounds max X */
  @field({ type: "f32", label: "Bounds Max X", group: "Confine" })
  boundsMaxX: number = 100;

  /** Bounds min Y */
  @field({ type: "f32", label: "Bounds Min Y", group: "Confine" })
  boundsMinY: number = -100;

  /** Bounds max Y */
  @field({ type: "f32", label: "Bounds Max Y", group: "Confine" })
  boundsMaxY: number = 100;

  // ========================================================================
  // LOOKAHEAD
  // ========================================================================

  /** Enable lookahead (predict target movement) */
  @field({ type: "bool", label: "Enable Lookahead" })
  lookaheadEnabled: boolean = false;

  /** Lookahead time (seconds to predict ahead) */
  @field({ type: "f32", label: "Lookahead Time", min: 0, max: 2, step: 0.01 })
  lookaheadTime: number = 0.3;

  /** Lookahead smoothing */
  @field({ type: "f32", label: "Lookahead Smoothing", min: 0, max: 30, step: 0.5 })
  lookaheadSmoothing: number = 5;

  /** Ignore Y for lookahead */
  @field({ type: "bool", label: "Lookahead Ignore Y" })
  lookaheadIgnoreY: boolean = false;

  // ========================================================================
  // RUNTIME STATE (@runtime → live scratch, never serialized; CameraRigBehaviour read/writes it)
  // ========================================================================

  /** Current internal camera position (for damping) */
  @runtime
  _currentPosition: { x: number; y: number } = { x: 0, y: 0 };

  /** Current internal camera rotation */
  @runtime
  _currentRotation: number = 0;

  /** Current ortho size (for lens damping) */
  @runtime
  _currentOrthoSize: number = 5;

  /** Lookahead velocity tracker */
  @runtime
  _targetVelocity: { x: number; y: number } = { x: 0, y: 0 };

  /** Previous target position for velocity calculation */
  @runtime
  _prevTargetPosition: { x: number; y: number } = { x: 0, y: 0 };

  /** Noise phase */
  @runtime
  _noisePhase: number = 0;

  /** Is this camera currently live */
  @runtime
  _isLive: boolean = false;
}
